import React from 'react';
import { Paper, Button, Typography, Box, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import FormField from '../Common/FormField';
import TracksAndServicesSection from '../Common/ProfessionalTracks/TracksAndServicesSection';
import AgreementsSection from '../Common/ProfessionalTracks/AgreementsSection';
import SkillsSection from '../Common/SkillsSection';
import StateDropdown from '../Common/StateDropdown';
import CityDropdown from '../Common/CityDropdown';
import { PersonalInfoFieldsProps, SkillEntry } from '../../types/profileForm.types';
import { UserInitialStateType } from 'DataTypes/Redux';
import { useSelector } from 'react-redux';
import ControlledForm from "Components/ControlledForm";
import CountryCodeSelect from "Components/CountryCodeSelect";

// Extended props type to include upload props if not already in types
interface ExtendedProps extends PersonalInfoFieldsProps {
  onFileChange?: (files: File[]) => void;
  onUpload?: () => void;
  files?: File[];
  isUploaded?: boolean;
  hasProfessional?: boolean;
  hasODR?: boolean;
  profAgreementAccepted?: boolean;
  odrAgreementAccepted?: boolean;
  declarationAccepted?: boolean;
  showDeclaration?: boolean;
  onTrackSelectionChange?: (hasProf: boolean, hasODR: boolean, selectedRolesArray: string[]) => void;
  setProfAgreementAccepted?: (val: boolean) => void;
  setOdrAgreementAccepted?: (val: boolean) => void;
  setDeclarationAccepted?: (val: boolean) => void;
}

const PersonalInfoFields: React.FC<ExtendedProps> = ({
  formData,
  errors,
  onChange,
  onBlur,
  onFileChange,
  onUpload,
  files = [],
  isUploaded = false,
  hasProfessional = false,
  hasODR = false,
  profAgreementAccepted = false,
  odrAgreementAccepted = false,
  declarationAccepted = false,
  showDeclaration = true,
  setProfAgreementAccepted = () => { },
  setOdrAgreementAccepted = () => { },
  setDeclarationAccepted = () => { },
  onTrackSelectionChange
}) => {
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );

  // Handle state change to clear city when state changes
  const handleStateChange = (name: string, value: string) => {
    // Clear city if state changes
    if (name === 'state' && value !== formData.state && formData.city) {
      onChange('city', '');
    }
    onChange(name, value);
  };

  // Handle skills section changes
  const handleSkillsChange = (skillEntries: SkillEntry[] | ((prev: SkillEntry[]) => SkillEntry[])) => {
    onChange('skillEntries', skillEntries);
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: '12px', mb: 2 }}>
      <Grid container columnSpacing={12} rowSpacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} >
          <FormField
            label="Name"
            name="name"
            type="text"
            value={formData.name}
            error={errors.name}
            placeholder="Enter your full name"
            required
            onChange={onChange}
            onBlur={onBlur}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} >
          <FormField
            label="Email Address"
            name="email"
            type="email"

            value={formData.email}
            error={errors.email}
            placeholder="Enter your email address"
            required
            onChange={onChange}
            onBlur={onBlur}
            disabled
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }} >
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
            {/* Label — matches FormField's label column */}
            <Typography
              variant="subtitle1"
              sx={{
                minWidth: { xs: '120px', sm: '200px' },
                flexShrink: 0,
                fontSize: '14px',
                fontWeight: 600,
                color: '#212529',
              }}
            >
              Phone Number
              <Typography component="span" sx={{ color: '#dc3545', ml: 0.5 }}>*</Typography>
            </Typography>

            {/* Inputs — country code + number inline */}
            <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box sx={{}}>
                <CountryCodeSelect
                  value={formData.phone?.match(/^(\+\d{1,4})/) ? (formData.phone.match(/^(\+\d{1,4})/)?.[1] || '+91') : '+91'}
                  disabled={!(myInfo?.userType === 'NRIUSER' && String(myInfo?.phone) === '0000000000')}
                  readOnly={!(myInfo?.userType === 'NRIUSER' && String(myInfo?.phone) === '0000000000')}
                  onChange={(val) => {
                    const num = formData.phone?.replace(/^(\+\d{1,4})/, '') || '';
                    onChange('phone', val + num);
                  }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  name="phone"
                  type="tel"
                  value={formData.phone?.replace(/^(\+\d{1,4})/, '') || ''}
                  placeholder="Enter your phone number"
                  fullWidth
                  disabled={!(myInfo?.userType === 'NRIUSER' && String(myInfo?.phone) === '0000000000')}
                  inputProps={{ maxLength: 10 }}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                      const code = formData.phone?.match(/^(\+\d{1,4})/) ? (formData.phone.match(/^(\+\d{1,4})/)?.[1] || '+91') : '+91';
                      onChange('phone', code + val);
                    }
                  }}
                  onBlur={() => onBlur('phone')}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '48px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      backgroundColor: !(myInfo?.userType === 'NRIUSER' && String(myInfo?.phone) === '0000000000') ? '#f8f9fa' : '#fff',
                      '& fieldset': { borderColor: errors.phone ? '#dc3545' : '#dee2e6', borderWidth: '1px' },
                      '&:hover fieldset': { borderColor: errors.phone ? '#dc3545' : '#007bff' },
                      '&.Mui-focused fieldset': { borderColor: errors.phone ? '#dc3545' : '#007bff', borderWidth: '2px' },
                      '& input': { padding: '12px 16px', fontSize: '16px' },
                    },
                    '& .MuiFormHelperText-root': { fontSize: '12px', color: '#dc3545', marginLeft: 0, marginTop: '4px' },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <FormField
            label="Date of Birth"
            name="dateOfBirth"
            type="date"
            value={formData.dateOfBirth}
            error={errors.dateOfBirth}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <FormField
            label="Address"
            name="address"
            type="text"
            value={formData.address}
            error={errors.address}
            placeholder="Enter your address"
            onChange={onChange}
            onBlur={onBlur}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <StateDropdown
            label="State"
            value={formData.state || ''}
            error={errors.state}
            onChange={handleStateChange}
            onBlur={onBlur}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <CityDropdown
            label="City"
            value={formData.city || ''}
            selectedState={formData.state || ''}
            error={errors.city}
            onChange={onChange}
            onBlur={onBlur}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <FormField
            label="Postal Code"
            name="postalCode"
            type="text"
            value={formData.postalCode}
            error={errors.postalCode}
            placeholder="Enter your postal code"
            onChange={onChange}
            onBlur={onBlur}
          />
        </Grid>


        {myInfo.userType === "CORPORATEUSER" && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <FormField
                label="Organisation Name"
                name="organisationName"
                type="text"
                value={formData.organisationName || ''}
                error={errors.organisationName}
                placeholder="Enter your organisation name"
                onChange={onChange}
                onBlur={onBlur}
                disabled
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <FormField
                label="Entity Type"
                name="entityType"
                type="text"
                value={formData.entityType || ''}
                error={errors.entityType}
                placeholder="E.g. Private Limited, LLP, Partnership"
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <FormField
                label="Authorised Person"
                name="authorisedPerson"
                type="text"
                value={formData.authorisedPerson || ''}
                error={errors.authorisedPerson}
                placeholder="Name of Authorised Person"
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <FormField
                label="Authorised Person Phone"
                name="authorisedPersonPhone"
                type="tel"
                value={formData.authorisedPersonPhone || ''}
                error={errors.authorisedPersonPhone}
                placeholder="Phone number"
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <FormField
                label="Registration Number (CIN/LLPIN)"
                name="registrationNumber"
                type="text"
                value={formData.registrationNumber || ''}
                error={errors.registrationNumber}
                placeholder="Enter CIN/LLPIN"
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <FormField
                label="GSTIN"
                name="gstin"
                type="text"
                value={formData.gstin || ''}
                error={errors.gstin}
                placeholder="Enter GSTIN"
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <FormField
                label="PAN"
                name="pan"
                type="text"
                value={formData.pan || ''}
                error={errors.pan}
                placeholder="Enter PAN"
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <FormField
                label="TAN"
                name="tan"
                type="text"
                value={formData.tan || ''}
                error={errors.tan}
                placeholder="Enter TAN"
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>
          </>
        )}

        {myInfo?.userType === 'PROFESSIONAL' && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <FormField
                label="Highest Qualification"
                name="highestQualification"
                type="text"
                value={formData.highestQualification || ''}
                error={errors.highestQualification}
                placeholder="Enter your highest qualification"
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>

            {/* Document Upload Section for Professionals */}
            <Grid size={{ xs: 12, sm: 6 }} >
              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                Verification Documents
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} >
              <ControlledForm.Input
                id="fileSelectionProfile"
                type="file"
                onChange={(value) =>
                  onFileChange && onFileChange(typeof value == "object" ? value : [])
                }
                label="Upload Professional Qualification Documents"
                focused={true}
                readOnly={isUploaded}
                {...(isUploaded && {
                  borderColor: "success",
                  infoText: "Documents Uploaded Successfully",
                  iconId: "check-icon",
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} >
              <Button
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: isUploaded ? "#4CAF50" : undefined,
                  "&:hover": {
                    backgroundColor: isUploaded
                      ? "#43A047"
                      : undefined,
                  },
                  height: '56px'
                }}
                onClick={onUpload}
                disabled={isUploaded || (files && files.length === 0)}
              >
                {isUploaded ? "Uploaded" : "Upload"}
              </Button>
            </Grid>

            {/* Display Uploaded Certificates */}
            {formData.uploadedCertificates && formData.uploadedCertificates.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Existing Verified Documents
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {formData.uploadedCertificates.map((cert) => {
                    const baseUrl = process.env.REACT_APP_BASE_URL || '';
                    const fileUrl = cert.file.startsWith('http')
                      ? cert.file
                      : `${baseUrl}${cert.file.startsWith('/') ? '' : '/'}${cert.file}`;

                    return (
                      <Box key={cert.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
                          Document Uploaded on {new Date(cert.uploadedAt).toLocaleDateString()}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(fileUrl, '_blank')}
                        >
                          View
                        </Button>
                      </Box>
                    )
                  })}
                </Box>
              </Grid>
            )}
            <Grid size={{ xs: 12 }} >
              <TracksAndServicesSection
                onTrackSelectionChange={onTrackSelectionChange!}
                selectedRolesArray={formData.professionalRoles}
              />
            </Grid>

            <Grid size={{ xs: 12 }} >
              <SkillsSection
                skillEntries={formData.skillEntries || []}
                errors={errors.skillEntries}
                onChange={handleSkillsChange}
                onBlur={onBlur}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }} >
              <FormField
                label="Description"
                name="description"
                type="textarea"
                value={formData.description}
                error={errors.description}
                placeholder="Write a short professional bio..."
                onChange={onChange}
                onBlur={onBlur}
              />
            </Grid>

            <Grid size={{ xs: 12 }} >
              <AgreementsSection
                hasProfessional={hasProfessional}
                hasODR={hasODR}
                profAgreementAccepted={profAgreementAccepted}
                setProfAgreementAccepted={setProfAgreementAccepted}
                odrAgreementAccepted={odrAgreementAccepted}
                setOdrAgreementAccepted={setOdrAgreementAccepted}
                declarationAccepted={declarationAccepted}
                setDeclarationAccepted={setDeclarationAccepted}
                showDeclaration={showDeclaration}
              />
            </Grid>

          </>
        )}
      </Grid>
    </Paper>
  );
};

export default PersonalInfoFields;